/**
 * Events.tsx — Multi-day YouTube event analytics
 */

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Cell, LabelList, ComposedChart, Line,
} from "recharts";
import { branding } from "../config/branding";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventDay { day: number; date: string; label: string; }

interface EventDef {
  id: string; name: string; platform: string;
  utmMedium: string; windowStart: string; windowEnd: string;
  days: EventDay[];
}

interface SourceBreakdown { source: string; optins: number; purchases: number; cash: number; }

interface EventFunnel {
  eventId: string; eventName: string;
  totalOptins: number; uniqueOptins: number;
  totalApps: number;
  fullApps: number;
  partialApps: number;
  totalPurchases: number; purchaseCash: number;
  totalCalls: number; totalShowed: number; totalCloses: number; eocCash: number;
  totalBookings: number;
  totalCash: number; paidCash: number;
  adSpend: number; roas: number; costPerOptin: number;
  cashPerOptin: number; cashPerApp: number; aov: number;
  optinToAppRate: number; optinToPurchaseRate: number;
  optinToCallRate: number; optinToCloseRate: number; optinToBookingRate: number;
  appToCallRate: number; appToCloseRate: number; appToBookingRate: number;
  showRate: number; closeRate: number; avgCashPerBooking: number;
  sourceBreakdown: SourceBreakdown[];
}

interface CashBySourceItem { source: string; cash: number; purchases: number; }
interface StageLead { email: string; name?: string; source?: string; amount?: number; product?: string; timestamp?: string; }

interface InvestmentBucket { bucket: string; total: number; closed: number; calledTotal: number; rate: number; callCloseRate: number; }
interface InvestmentStats {
  applicantIncomeBreakdown: { name: string; value: number }[];
  investmentCloseRates: InvestmentBucket[];
  dailyAppTrend: { date: string; total: number }[];
}
interface CalendarAdd { day: number; count: number; }

interface ZoomAttendee { name: string; email: string; country: string; totalMinutes: number; sessions: number; }
interface ZoomSessionData {
  eventId: string; webinarId: string; topic: string; startTime: string; durationMinutes: number;
  registrants: number; uniqueViewers: number; totalSessions: number; maxConcurrent: number;
  attendees: ZoomAttendee[];
  retentionCurve: { minute: number; count: number }[];
  countryBreakdown: { country: string; count: number }[];
  durationBuckets: { label: string; count: number }[];
}

// ─── Info text ────────────────────────────────────────────────────────────────

const INFO_TEXT: Record<string, string> = {
  "Total Opt-ins": "All form submissions tagged with this event's UTM medium. Includes duplicates.",
  "Unique Opt-ins": "Deduplicated by email. Used as base for all conversion rates.",
  "Applications": "Applications submitted by anyone whose email matches an event opt-in.",
  "Closes": "Fanbasis/Whop purchases + EOC sales call closes matched by opt-in email.",
  "Calls": "Total EOC calls taken (showed up) / total that closed.",
  "Cash Collected": "Total cash from Fanbasis/Whop purchases + EOC sales call closes, matched by email.",
  "Cash / Opt-in": "Total Cash ÷ Unique Opt-ins.",
  "Cash / Application": "Total Cash ÷ Total Applications.",
  "AOV": "Average Order Value — Total Cash ÷ (Purchases + EOC Closes).",
  "Ad Spend": "Total paid ad spend for this event. Set manually to calculate ROAS and CPO.",
  "ROAS": "Paid Cash ÷ Ad Spend. Uses the \"Paid Cash (Meta)\" figure you entered if set, otherwise estimates from email attribution.",
  "Cost / Opt-in": "Ad Spend ÷ Unique Opt-ins.",
  "Total Bookings": "Sales calls booked by people whose email matches an event opt-in.",
  "Booking Rate": "Opt-in → Booking % and Application → Booking % side by side.",
  "Avg Cash / Booking": "Total Cash ÷ Total Bookings.",
  "Show Rate": "% of booked calls that showed up (not no-show, rescheduled, or cancelled).",
  "Close Rate": "% of showed calls that resulted in a close.",
  "Opt-in → Close %": "% of unique opt-ins who closed.",
  "App → Close %": "% of applicants who closed.",
  "Opt-in → App %": "% of unique opt-ins who submitted an application.",
  "Add to Calendar": "People who added this event to their Google Calendar. Input per day.",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 0) {
  return n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
function fmtCash(n: number) {
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return "$" + Math.round(n / 1_000) + "k";
  return "$" + Math.round(n);
}
function fmtCashFull(n: number) {
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtPct(n: number) { return n.toFixed(1) + "%"; }
function fmtX(n: number) { return n.toFixed(2) + "×"; }

// ─── InfoButton ───────────────────────────────────────────────────────────────

function InfoButton({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="w-4 h-4 rounded-full border border-muted-foreground/40 text-[10px] text-muted-foreground/60 hover:text-foreground hover:border-foreground/40 flex items-center justify-center leading-none transition-colors"
      >i</button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
              className="absolute right-0 top-6 z-50 w-60 rounded-lg border border-border/50 bg-card p-3 text-xs text-muted-foreground shadow-xl"
            >{text}</motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Standard Metric Card ─────────────────────────────────────────────────────

interface MetricCardProps {
  label: string; value: string; sub?: string;
  highlight?: boolean; onClick?: () => void; onHide: () => void; infoText?: string;
}

function MetricCard({ label, value, sub, highlight, onClick, onHide, infoText }: MetricCardProps) {
  return (
    <div
      className={`relative group rounded-xl border p-4 transition-all ${
        highlight ? "bg-[var(--brand-primary)]/10 border-[var(--brand-primary)]/30" : "bg-muted/20 border-border/40"
      } ${onClick ? "cursor-pointer hover:border-[var(--brand-primary)]/50 hover:bg-[var(--brand-primary)]/5" : ""}`}
      onClick={onClick}
    >
      {infoText && <div className="absolute top-2 right-7"><InfoButton text={infoText} /></div>}
      <button
        onClick={(e) => { e.stopPropagation(); onHide(); }}
        className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-muted/40 text-muted-foreground/50 hover:bg-destructive/20 hover:text-destructive text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
        title="Hide card"
      >×</button>
      <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-2xl font-bold ${highlight ? "text-[var(--brand-primary)]" : "text-foreground"}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
      {onClick && <div className="text-[10px] text-[var(--brand-primary)]/60 mt-1.5 flex items-center gap-1"><span>↗</span> View leads</div>}
    </div>
  );
}

// ─── Apps Card (total + partial/full split toggle) ───────────────────────────

function AppsCard({ label, value, sub, fullApps, partialApps, onClick, onHide, infoText }: {
  label: string; value: string; sub?: string;
  fullApps: number; partialApps: number;
  onClick?: () => void; onHide: () => void; infoText?: string;
}) {
  const [split, setSplit] = useState(false);
  return (
    <div
      className={`relative group rounded-xl border p-4 transition-all bg-muted/20 border-border/40 ${!split && onClick ? "cursor-pointer hover:border-[var(--brand-primary)]/50 hover:bg-[var(--brand-primary)]/5" : ""}`}
      onClick={!split && onClick ? onClick : undefined}
    >
      {/* Split toggle icon — top left */}
      <button
        onClick={(e) => { e.stopPropagation(); setSplit((s) => !s); }}
        className={`absolute top-2 left-2 w-5 h-5 rounded flex items-center justify-center transition-all ${split ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground/40 hover:text-[var(--brand-primary)]"}`}
        title={split ? "Show total" : "Split by full / partial"}
      >
        <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="3.5" cy="3.5" r="1.5" />
          <circle cx="3.5" cy="12.5" r="1.5" />
          <circle cx="12.5" cy="8" r="1.5" />
          <path d="M5 3.5h2a4 4 0 0 1 4 4M5 12.5h2a4 4 0 0 0 4-4" />
        </svg>
      </button>
      {infoText && <div className="absolute top-2 right-7"><InfoButton text={infoText} /></div>}
      <button
        onClick={(e) => { e.stopPropagation(); onHide(); }}
        className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-muted/40 text-muted-foreground/50 hover:bg-destructive/20 hover:text-destructive text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
        title="Hide card"
      >×</button>
      <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-1 ml-5">{label}</div>
      {split ? (
        <div className="space-y-1.5">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-foreground">{fullApps.toLocaleString()}</span>
            <span className="text-[11px] text-emerald-500 font-medium">Full</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-foreground">{partialApps.toLocaleString()}</span>
            <span className="text-[11px] text-amber-500 font-medium">Partial only</span>
          </div>
        </div>
      ) : (
        <>
          <div className="text-2xl font-bold text-foreground">{value}</div>
          {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
          {onClick && <div className="text-[10px] text-[var(--brand-primary)]/60 mt-1.5 flex items-center gap-1"><span>↗</span> View leads</div>}
        </>
      )}
    </div>
  );
}

// ─── Split Card (two-tab toggle) ──────────────────────────────────────────────

function SplitCard({
  label, tabA, valA, tabB, valB, subA, subB, onHide, infoText,
}: {
  label: string; tabA: string; valA: string; tabB: string; valB: string;
  subA?: string; subB?: string; onHide: () => void; infoText?: string;
}) {
  const [showA, setShowA] = useState(true);
  return (
    <div className="relative group rounded-xl border bg-muted/20 border-border/40 p-4 transition-all">
      {infoText && <div className="absolute top-2 right-7"><InfoButton text={infoText} /></div>}
      <button
        onClick={(e) => { e.stopPropagation(); onHide(); }}
        className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-muted/40 text-muted-foreground/50 hover:bg-destructive/20 hover:text-destructive text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
        title="Hide card"
      >×</button>
      <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-2">{label}</div>
      <div className="flex gap-1 mb-2">
        {([{ key: true, text: tabA }, { key: false, text: tabB }] as const).map(({ key, text }) => (
          <button
            key={String(key)}
            onClick={() => setShowA(key)}
            className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-all ${
              showA === key
                ? "bg-[var(--brand-primary)] text-white border-transparent"
                : "border-border/50 text-muted-foreground hover:border-[var(--brand-primary)]/40"
            }`}
          >{text}</button>
        ))}
      </div>
      <div className="text-2xl font-bold text-foreground">{showA ? valA : valB}</div>
      {(showA ? subA : subB) && (
        <div className="text-[11px] text-muted-foreground mt-0.5">{showA ? subA : subB}</div>
      )}
    </div>
  );
}

// ─── Calls Card (Due | Taken | Closed) ────────────────────────────────────────

function CallsCard({
  scheduled, taken, closed, closeRate, showRate, onHide, infoText,
}: {
  scheduled: number; taken: number; closed: number;
  closeRate: number; showRate: number;
  onHide: () => void; infoText?: string;
}) {
  const [view, setView] = useState<"due" | "taken" | "closed">("due");
  const tabs = [{ key: "due" as const, label: "Due" }, { key: "taken" as const, label: "Taken" }, { key: "closed" as const, label: "Closed" }];

  return (
    <div className="relative group rounded-xl border bg-muted/20 border-border/40 p-4 transition-all">
      {infoText && <div className="absolute top-2 right-7"><InfoButton text={infoText} /></div>}
      <button
        onClick={(e) => { e.stopPropagation(); onHide(); }}
        className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-muted/40 text-muted-foreground/50 hover:bg-destructive/20 hover:text-destructive text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
        title="Hide card"
      >×</button>
      <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-2">Calls</div>
      <div className="flex gap-1 mb-3">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setView(t.key)}
            className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-all ${
              view === t.key
                ? "bg-[var(--brand-primary)] text-white border-transparent"
                : "border-border/50 text-muted-foreground hover:border-[var(--brand-primary)]/40"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {view === "due" && (
        <>
          <div className="text-2xl font-bold text-foreground">{fmt(scheduled)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">total EOC calls</div>
        </>
      )}
      {view === "taken" && (
        <>
          <div className="text-2xl font-bold text-foreground">{fmt(taken)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{fmtPct(showRate)} show rate</div>
        </>
      )}
      {view === "closed" && (
        <>
          <div className="text-2xl font-bold text-foreground">{fmt(closed)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{fmtPct(closeRate)} close rate</div>
        </>
      )}
    </div>
  );
}

// ─── Calendar Adds Card ───────────────────────────────────────────────────────

function CalendarAddsCard({
  eventId, uniqueOptins, onHide, infoText,
}: {
  eventId: string; uniqueOptins: number; onHide: () => void; infoText?: string;
}) {
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`/api/events/calendar-adds/${eventId}`)
      .then(r => r.json())
      .then((d: { success: boolean; adds: CalendarAdd[] }) => {
        if (d.success) {
          const sum = d.adds.reduce((s, a) => s + a.count, 0);
          setTotal(sum);
          setInput(String(sum));
        }
      })
      .catch(() => {});
  }, [eventId]);

  const pct = uniqueOptins > 0 && total > 0 ? (total / uniqueOptins) * 100 : 0;

  async function saveAdds() {
    const count = parseInt(input) || 0;
    setSaving(true);
    try {
      const res = await fetch(`/api/events/calendar-adds/${eventId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adds: [{ day: 0, count }] }),
      });
      const data = await res.json() as { success: boolean };
      if (data.success) { setTotal(count); setEditing(false); }
    } catch { /* silent */ }
    setSaving(false);
  }

  return (
    <div className="relative group rounded-xl border bg-muted/20 border-border/40 p-4 transition-all col-span-1">
      {infoText && <div className="absolute top-2 right-7"><InfoButton text={infoText} /></div>}
      <button
        onClick={(e) => { e.stopPropagation(); onHide(); }}
        className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-muted/40 text-muted-foreground/50 hover:bg-destructive/20 hover:text-destructive text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
        title="Hide card"
      >×</button>
      <div className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-1">Add to Calendar</div>

      {editing ? (
        <div className="mt-1 space-y-2">
          <input
            type="number" min="0"
            value={input}
            onChange={e => setInput(e.target.value)}
            className="w-full px-2 py-1.5 text-sm rounded border border-border bg-muted/20 focus:outline-none focus:border-[var(--brand-primary)]/50"
            placeholder="Total adds"
            autoFocus
          />
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="px-2 py-1 text-[10px] rounded border border-border hover:bg-muted transition-colors">Cancel</button>
            <button
              onClick={saveAdds} disabled={saving}
              className="px-2 py-1 text-[10px] rounded bg-[var(--brand-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >{saving ? "…" : "Save"}</button>
          </div>
        </div>
      ) : (
        <>
          {total > 0 ? (
            <>
              <div className="text-2xl font-bold text-foreground">{fmt(total)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{fmtPct(pct)} of opt-ins</div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground/60 mt-1">No data yet</div>
          )}
          <button
            onClick={() => setEditing(true)}
            className="mt-2 text-[10px] text-[var(--brand-primary)] hover:underline"
          >✎ Edit</button>
        </>
      )}
    </div>
  );
}

// ─── Stage Leads Modal ────────────────────────────────────────────────────────

function StageLeadsModal({ eventId, stage, label, onClose }: {
  eventId: string; stage: string; label: string; onClose: () => void;
}) {
  const [leads, setLeads] = useState<StageLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch(`/api/events/leads/${eventId}/${stage}`)
      .then(r => r.json())
      .then((d: { success: boolean; leads: StageLead[] }) => { if (d.success) setLeads(d.leads); setLoading(false); })
      .catch(() => setLoading(false));
  }, [eventId, stage]);

  const filtered = leads.filter(l =>
    !search || l.email.includes(search.toLowerCase()) || (l.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-semibold text-lg">{label}</h2>
            <p className="text-sm text-muted-foreground">{leads.length.toLocaleString()} leads</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center text-lg">×</button>
        </div>
        <div className="p-4 border-b border-border">
          <input
            type="text" placeholder="Search by email or name..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg bg-muted/40 border border-border focus:outline-none focus:border-[var(--brand-primary)]/50"
          />
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-1.5">
          {loading ? (
            <div className="text-center text-muted-foreground py-8 text-sm">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">No leads found</div>
          ) : filtered.map((lead, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 bg-muted/20 hover:bg-muted/40 transition-colors">
              <div className="min-w-0">
                {lead.name && <div className="text-sm font-medium truncate">{lead.name}</div>}
                <div className="text-xs text-muted-foreground truncate">{lead.email}</div>
                {lead.source && <div className="text-[10px] text-muted-foreground/60">{lead.source}</div>}
              </div>
              <div className="text-right ml-3 shrink-0">
                {lead.amount != null && <div className="text-sm font-semibold text-emerald-400">{fmtCashFull(lead.amount)}</div>}
                {lead.product && <div className="text-[10px] text-muted-foreground/70 max-w-[120px] truncate">{lead.product}</div>}
                {lead.timestamp && <div className="text-[10px] text-muted-foreground/60">{new Date(lead.timestamp).toLocaleDateString()}</div>}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Visual Funnel ────────────────────────────────────────────────────────────

interface FunnelStageData {
  label: string; value: number; subLabel?: string; isCash?: boolean;
  onClick?: () => void;
}

function VisualFunnel({ stages }: { stages: FunnelStageData[] }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const FULL_W = 480;
  const STAGE_H = 58;
  const TIP_W = 52;
  const GAP = 2;
  const LABEL_PAD = 110;
  const CONV_PAD = 60;
  const totalW = FULL_W + LABEL_PAD + CONV_PAD;
  const totalH = stages.length * (STAGE_H + GAP);

  // Fixed triangle — evenly stepped from full width down to a point
  const n = stages.length;
  const stageWidths = stages.map((_, i) => {
    const t = n <= 1 ? 1 : i / (n - 1);
    return Math.round(FULL_W - t * (FULL_W - TIP_W));
  });

  return (
    <svg
      viewBox={`0 0 ${totalW} ${totalH}`}
      className="w-full"
      style={{ height: Math.min(totalH, 500) }}
    >
      <defs>
        <linearGradient id="evFunnelGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={branding.colors.gradient.start} stopOpacity={0.92} />
          <stop offset="100%" stopColor={branding.colors.gradient.end} stopOpacity={0.92} />
        </linearGradient>
      </defs>

      {stages.map((stage, i) => {
        const topW    = stageWidths[i];
        const bottomW = i < n - 1 ? stageWidths[i + 1] : 0;
        const fLeft   = LABEL_PAD;
        const topLeft    = fLeft + (FULL_W - topW) / 2;
        const topRight   = fLeft + (FULL_W + topW) / 2;
        const bottomLeft  = fLeft + (FULL_W - bottomW) / 2;
        const bottomRight = fLeft + (FULL_W + bottomW) / 2;
        const y  = i * (STAGE_H + GAP);
        const cy = y + STAGE_H / 2;
        const pts = `${topLeft},${y} ${topRight},${y} ${bottomRight},${y + STAGE_H} ${bottomLeft},${y + STAGE_H}`;

        // Don't show a conversion % for the cash stage — dollars ÷ count = garbage
        const convPct =
          i > 0 && !stage.isCash && stages[i - 1].value > 0
            ? `${Math.round((stage.value / stages[i - 1].value) * 100)}%`
            : null;

        const opacity  = Math.max(0.5, 1 - i * 0.055);
        const isHovered = hoveredIdx === i;

        return (
          <g
            key={stage.label}
            onClick={stage.onClick}
            onMouseEnter={() => stage.onClick && setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{ cursor: stage.onClick ? "pointer" : "default" }}
          >
            <polygon points={pts} fill="url(#evFunnelGrad)" opacity={opacity} />
            {isHovered && <polygon points={pts} fill="white" opacity={0.1} />}

            {/* Stage label — left */}
            <text
              x={topLeft - 8} y={cy + 1}
              textAnchor="end" dominantBaseline="middle"
              fontSize={11} fontFamily="sans-serif"
              fill="rgba(255,255,255,0.6)"
            >
              {stage.label}
            </text>

            {/* Primary value — center */}
            <text
              x={fLeft + FULL_W / 2}
              y={stage.subLabel ? cy - 8 : cy}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={14} fontWeight="bold" fontFamily="sans-serif"
              fill="white"
            >
              {stage.isCash ? fmtCashFull(stage.value) : stage.value.toLocaleString()}
            </text>

            {stage.subLabel && (
              <text
                x={fLeft + FULL_W / 2} y={cy + 9}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={9} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.55)"
              >
                {stage.subLabel}
              </text>
            )}

            {isHovered && stage.onClick && !stage.isCash && (
              <text
                x={fLeft + FULL_W / 2}
                y={stage.subLabel ? cy + 22 : cy + 14}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={8} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.6)"
              >
                click to view leads
              </text>
            )}

            {/* Conversion % — right */}
            {convPct && (
              <text
                x={topRight + 8} y={cy + 1}
                textAnchor="start" dominantBaseline="middle"
                fontSize={10} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.4)"
              >
                {convPct}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Configure Days Panel ─────────────────────────────────────────────────────

function ConfigureDaysPanel({ eventId, currentDays, onSaved, onClose }: {
  eventId: string; currentDays: EventDay[]; onSaved: (days: EventDay[]) => void; onClose: () => void;
}) {
  const [dayCount, setDayCount] = useState<3 | 4>(currentDays.length === 4 ? 4 : 3);
  const toLocal = (iso: string) => iso.slice(0, 10);
  const [dates, setDates] = useState<string[]>([
    currentDays[0] ? toLocal(currentDays[0].date) : "",
    currentDays[1] ? toLocal(currentDays[1].date) : "",
    currentDays[2] ? toLocal(currentDays[2].date) : "",
    currentDays[3] ? toLocal(currentDays[3].date) : "",
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    const relevant = dates.slice(0, dayCount);
    if (relevant.some(d => !d)) { setError(`Please fill all ${dayCount} dates.`); return; }
    setSaving(true); setError("");
    const payload = relevant.map((dateStr, i) => ({ day: i + 1, date: new Date(dateStr).toISOString(), label: `Day ${i + 1}` }));
    try {
      const res = await fetch(`/api/events/days/${eventId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days: payload }),
      });
      const d = await res.json() as { success: boolean };
      if (d.success) { onSaved(payload.map(p => ({ ...p, date: p.date }))); onClose(); }
      else setError("Save failed");
    } catch { setError("Network error"); }
    setSaving(false);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      className="rounded-2xl border border-[var(--brand-primary)]/30 bg-card p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Configure Event Days</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg w-6 h-6 flex items-center justify-center">×</button>
      </div>
      <div className="flex gap-2">
        {([3, 4] as const).map(n => (
          <button key={n} onClick={() => setDayCount(n)}
            className={`px-5 py-2 rounded-lg text-sm font-medium border transition-all ${dayCount === n ? "bg-[var(--brand-primary)] text-white border-transparent" : "border-border text-muted-foreground hover:border-[var(--brand-primary)]/50"}`}
          >{n} Days</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: dayCount }, (_, i) => (
          <div key={i} className="space-y-1">
            <label className="text-xs text-muted-foreground font-medium">Day {i + 1}</label>
            <input type="date" value={dates[i] ?? ""}
              onChange={e => setDates(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
              className="w-full px-2.5 py-2 text-sm rounded-lg border border-border bg-muted/20 focus:outline-none focus:border-[var(--brand-primary)]/50"
            />
          </div>
        ))}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-3 justify-end">
        <button onClick={onClose} className="px-4 py-1.5 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
        <button onClick={save} disabled={saving} className="px-4 py-1.5 text-sm rounded-lg bg-[var(--brand-primary)] text-white hover:opacity-90 disabled:opacity-50">
          {saving ? "Saving…" : "Save Days"}
        </button>
      </div>
    </motion.div>
  );
}

// ─── Investment Gauge ─────────────────────────────────────────────────────────

function InvestmentGauge({ bucket, total, closed, rate, sublabel = "closed", totalLabel = "leads" }: {
  bucket: string; total: number; closed: number; rate: number; sublabel?: string; totalLabel?: string;
}) {
  const size = 160;
  const strokeWidth = 14;
  const r = (size - strokeWidth) / 2 - 4;
  const cx = size / 2;
  const cy = size / 2;
  function pt(angleDeg: number): [number, number] {
    const rad = (angleDeg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  }
  const startAngle = 135;
  const totalSweep = 270;
  const [sx, sy] = pt(startAngle);
  const [ex, ey] = pt(45);
  const trackD = `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 1 1 ${ex.toFixed(2)} ${ey.toFixed(2)}`;
  let fillD = "";
  if (rate > 0) {
    const fillAngle = startAngle + (totalSweep * rate) / 100;
    const [fx, fy] = pt(fillAngle);
    const fillSweep = (totalSweep * rate) / 100;
    const largeArc = fillSweep > 180 ? 1 : 0;
    fillD = `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${fx.toFixed(2)} ${fy.toFixed(2)}`;
  }
  const fillColor = rate >= 50 ? branding.colors.chart[0] : rate >= 25 ? branding.colors.chart[2] : branding.colors.chart[4];
  const shortLabel = bucket.replace(" (We cannot help you)", "");
  return (
    <div className="flex flex-col items-center rounded-xl border border-border/40 bg-card p-4 gap-1">
      <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest text-center leading-tight mb-1">{shortLabel}</div>
      <svg width={size} height={Math.round(size * 0.72)} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible" }}>
        <path d={trackD} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" opacity={0.1} />
        {rate > 0 && <path d={fillD} fill="none" stroke={fillColor} strokeWidth={strokeWidth} strokeLinecap="round" />}
        <text x={cx} y={cy + 8} textAnchor="middle" fontSize="26" fontWeight="800" fill="currentColor">{rate}%</text>
        <text x={cx} y={cy + 26} textAnchor="middle" fontSize="10" fill="currentColor" opacity={0.5}>{sublabel}</text>
      </svg>
      <div className="flex gap-3 text-xs text-muted-foreground mt-1">
        <span><span className="font-bold text-foreground">{total.toLocaleString()}</span> {totalLabel}</span>
        <span><span className="font-bold" style={{ color: fillColor }}>{closed.toLocaleString()}</span> closed</span>
      </div>
    </div>
  );
}

// ─── Event logo map ───────────────────────────────────────────────────────────

const EVENT_LOGOS: Record<string, string> = {
  E001: "/cc-logo.png",
};

// ─── Event List View ──────────────────────────────────────────────────────────

// ─── YouTube Day Stats ────────────────────────────────────────────────────────

interface YouTubeDayStats {
  day: number;
  peakViewers?: number; avgViewers?: number; streamDurationSecs?: number;
  uniqueViewers?: number; views?: number; impressions?: number; impressionsCtr?: number;
  watchTimeHours?: number; avgViewDurationSecs?: number; avgPctViewed?: number;
  newViewersPct?: number; casualViewersPct?: number; regularViewersPct?: number;
  malePct?: number; femalePct?: number;
  age13_17Pct?: number; age18_24Pct?: number; age25_34Pct?: number; age35_44Pct?: number; age45_54Pct?: number; age55_64Pct?: number; age65plusPct?: number;
  subscribedPct?: number; notSubscribedPct?: number;
  externalTrafficPct?: number; directTrafficPct?: number; otherYtTrafficPct?: number; browseTrafficPct?: number; notificationsTrafficPct?: number; othersTrafficPct?: number;
}

function fmtDuration(secs: number | undefined): string {
  if (!secs) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function parseDuration(val: string): number {
  const parts = val.split(":").map(Number);
  if (parts.length === 3) return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
  if (parts.length === 2) return (parts[0] || 0) * 60 + (parts[1] || 0);
  return Number(val) || 0;
}

function YouTubeEditModal({ eventId, day, dayLabel, existing, onClose, onSaved }: {
  eventId: string; day: number; dayLabel: string;
  existing?: YouTubeDayStats;
  onClose: () => void; onSaved: (stats: YouTubeDayStats) => void;
}) {
  const [fields, setFields] = useState({
    peakViewers: String(existing?.peakViewers ?? ""),
    avgViewers: String(existing?.avgViewers ?? ""),
    streamDuration: fmtDuration(existing?.streamDurationSecs).replace("—", ""),
    uniqueViewers: String(existing?.uniqueViewers ?? ""),
    views: String(existing?.views ?? ""),
    impressions: String(existing?.impressions ?? ""),
    impressionsCtr: String(existing?.impressionsCtr ?? ""),
    watchTimeHours: String(existing?.watchTimeHours ?? ""),
    avgViewDuration: fmtDuration(existing?.avgViewDurationSecs).replace("—", ""),
    avgPctViewed: String(existing?.avgPctViewed ?? ""),
    newViewersPct: String(existing?.newViewersPct ?? ""),
    casualViewersPct: String(existing?.casualViewersPct ?? ""),
    regularViewersPct: String(existing?.regularViewersPct ?? ""),
    malePct: String(existing?.malePct ?? ""),
    femalePct: String(existing?.femalePct ?? ""),
    age13_17Pct: String(existing?.age13_17Pct ?? ""),
    age18_24Pct: String(existing?.age18_24Pct ?? ""),
    age25_34Pct: String(existing?.age25_34Pct ?? ""),
    age35_44Pct: String(existing?.age35_44Pct ?? ""),
    age45_54Pct: String(existing?.age45_54Pct ?? ""),
    age55_64Pct: String(existing?.age55_64Pct ?? ""),
    age65plusPct: String(existing?.age65plusPct ?? ""),
    subscribedPct: String(existing?.subscribedPct ?? ""),
    notSubscribedPct: String(existing?.notSubscribedPct ?? ""),
    externalTrafficPct: String(existing?.externalTrafficPct ?? ""),
    directTrafficPct: String(existing?.directTrafficPct ?? ""),
    otherYtTrafficPct: String(existing?.otherYtTrafficPct ?? ""),
    browseTrafficPct: String(existing?.browseTrafficPct ?? ""),
    notificationsTrafficPct: String(existing?.notificationsTrafficPct ?? ""),
    othersTrafficPct: String(existing?.othersTrafficPct ?? ""),
  });
  const [saving, setSaving] = useState(false);

  const num = (v: string) => v.trim() !== "" ? parseFloat(v) : undefined;

  async function save() {
    setSaving(true);
    const payload: Omit<YouTubeDayStats, "day"> = {
      peakViewers: num(fields.peakViewers), avgViewers: num(fields.avgViewers),
      streamDurationSecs: fields.streamDuration ? parseDuration(fields.streamDuration) : undefined,
      uniqueViewers: num(fields.uniqueViewers), views: num(fields.views),
      impressions: num(fields.impressions), impressionsCtr: num(fields.impressionsCtr),
      watchTimeHours: num(fields.watchTimeHours),
      avgViewDurationSecs: fields.avgViewDuration ? parseDuration(fields.avgViewDuration) : undefined,
      avgPctViewed: num(fields.avgPctViewed),
      newViewersPct: num(fields.newViewersPct), casualViewersPct: num(fields.casualViewersPct), regularViewersPct: num(fields.regularViewersPct),
      malePct: num(fields.malePct), femalePct: num(fields.femalePct),
      age13_17Pct: num(fields.age13_17Pct), age18_24Pct: num(fields.age18_24Pct), age25_34Pct: num(fields.age25_34Pct), age35_44Pct: num(fields.age35_44Pct),
      age45_54Pct: num(fields.age45_54Pct), age55_64Pct: num(fields.age55_64Pct), age65plusPct: num(fields.age65plusPct),
      subscribedPct: num(fields.subscribedPct), notSubscribedPct: num(fields.notSubscribedPct),
      externalTrafficPct: num(fields.externalTrafficPct), directTrafficPct: num(fields.directTrafficPct),
      otherYtTrafficPct: num(fields.otherYtTrafficPct), browseTrafficPct: num(fields.browseTrafficPct),
      notificationsTrafficPct: num(fields.notificationsTrafficPct), othersTrafficPct: num(fields.othersTrafficPct),
    };
    await fetch(`/api/events/youtube/${eventId}/${day}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    setSaving(false);
    onSaved({ day, ...payload });
    onClose();
  }

  const inp = (label: string, key: keyof typeof fields, placeholder?: string) => (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
      <input
        type="text" value={fields[key]} placeholder={placeholder}
        onChange={e => setFields(p => ({ ...p, [key]: e.target.value }))}
        className="bg-muted/30 border border-border/50 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[var(--brand-primary)]/60 w-full"
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">YouTube Stats — {dayLabel}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg leading-none">×</button>
        </div>
        <p className="text-xs text-muted-foreground">Enter numbers directly from YouTube Studio. Durations as <span className="font-mono">mm:ss</span> or <span className="font-mono">h:mm:ss</span>.</p>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Live Stream</p>
          <div className="grid grid-cols-3 gap-3">
            {inp("Peak Viewers", "peakViewers", "1319")}
            {inp("Avg Viewers", "avgViewers", "1091")}
            {inp("Duration", "streamDuration", "2:39:00")}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Reach</p>
          <div className="grid grid-cols-2 gap-3">
            {inp("Unique Viewers", "uniqueViewers", "3900")}
            {inp("Views", "views", "1100")}
            {inp("Impressions", "impressions", "23700")}
            {inp("Impressions CTR (%)", "impressionsCtr", "8.0")}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Engagement</p>
          <div className="grid grid-cols-3 gap-3">
            {inp("Watch Time (hrs)", "watchTimeHours", "159.2")}
            {inp("Avg View Duration", "avgViewDuration", "25:34")}
            {inp("Avg % Viewed", "avgPctViewed", "16.0")}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Audience</p>
          <div className="grid grid-cols-3 gap-3">
            {inp("New Viewers (%)", "newViewersPct", "38.3")}
            {inp("Casual Viewers (%)", "casualViewersPct", "45.8")}
            {inp("Regular Viewers (%)", "regularViewersPct", "15.9")}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Demographics</p>
          <div className="grid grid-cols-2 gap-3">
            {inp("Male (%)", "malePct", "89")}
            {inp("Female (%)", "femalePct", "11")}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {inp("Age 13–17 (%)", "age13_17Pct", "8.6")}
            {inp("Age 18–24 (%)", "age18_24Pct", "26.2")}
            {inp("Age 25–34 (%)", "age25_34Pct", "45.8")}
            {inp("Age 35–44 (%)", "age35_44Pct", "12.4")}
            {inp("Age 45–54 (%)", "age45_54Pct", "")}
            {inp("Age 55–64 (%)", "age55_64Pct", "")}
            {inp("Age 65+ (%)", "age65plusPct", "")}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Subscribers</p>
          <div className="grid grid-cols-2 gap-3">
            {inp("Subscribed (%)", "subscribedPct", "38.4")}
            {inp("Not Subscribed (%)", "notSubscribedPct", "61.6")}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Traffic Sources</p>
          <div className="grid grid-cols-3 gap-3">
            {inp("External (%)", "externalTrafficPct", "46.2")}
            {inp("Direct / URL (%)", "directTrafficPct", "22.6")}
            {inp("Other YouTube (%)", "otherYtTrafficPct", "15.3")}
            {inp("Browse Features (%)", "browseTrafficPct", "11.0")}
            {inp("Notifications (%)", "notificationsTrafficPct", "3.1")}
            {inp("Others (%)", "othersTrafficPct", "1.9")}
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-border/60 text-sm text-muted-foreground hover:border-border transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function YouTubeSection({ eventId, days, selectedDay, youtubeStats, optinsByDay, appsByDay, onEdit, zoomSession }: {
  eventId: string; days: EventDay[]; selectedDay: number;
  youtubeStats: YouTubeDayStats[]; optinsByDay: Record<number, number>; appsByDay: Record<number, number>;
  onEdit: (day: number) => void; zoomSession?: ZoomSessionData | null;
}) {
  const [ytViewDay, setYtViewDay] = useState<number>(0);
  useEffect(() => { setYtViewDay(selectedDay > 0 ? selectedDay : (days[0]?.day ?? 1)); }, [selectedDay, days]);
  const viewDay = ytViewDay > 0 ? ytViewDay : (days[0]?.day ?? 1);
  const stat = youtubeStats.find(s => s.day === viewDay);
  const dayLabel = days.find(d => d.day === viewDay)?.label ?? `Day ${viewDay}`;
  const optins = optinsByDay[viewDay] ?? 0;
  const apps = appsByDay[viewDay] ?? 0;
  const showZoom = !stat && !!zoomSession;

  // Zoom computed values (used when showing Day 4 Zoom data inline)
  const zoomAvgMinutes = zoomSession && zoomSession.attendees.length > 0
    ? Math.round(zoomSession.attendees.reduce((s, a) => s + a.totalMinutes, 0) / zoomSession.attendees.length) : 0;
  const zoomShowRate = zoomSession && zoomSession.registrants > 0
    ? ((zoomSession.uniqueViewers / zoomSession.registrants) * 100).toFixed(1) : "—";
  const zoomTopAttendees = zoomSession
    ? [...zoomSession.attendees].sort((a, b) => b.totalMinutes - a.totalMinutes).slice(0, 10) : [];
  const zoomMaxCountry = zoomSession?.countryBreakdown.length ? zoomSession.countryBreakdown[0].count : 1;
  const zoomMaxBucket = zoomSession?.durationBuckets.length
    ? Math.max(...zoomSession.durationBuckets.map(b => b.count)) : 1;

  const ageBuckets: [string, number][] = stat ? (
    [["13–17", stat.age13_17Pct], ["18–24", stat.age18_24Pct], ["25–34", stat.age25_34Pct],
     ["35–44", stat.age35_44Pct], ["45–54", stat.age45_54Pct], ["55–64", stat.age55_64Pct], ["65+", stat.age65plusPct]]
      .filter((x): x is [string, number] => x[1] != null)
      .sort((a, b) => b[1] - a[1])
  ) : [];
  const trafficSources: [string, number][] = stat ? (
    [["External", stat.externalTrafficPct], ["Direct / URL", stat.directTrafficPct],
     ["Other YouTube", stat.otherYtTrafficPct], ["Browse", stat.browseTrafficPct],
     ["Notifications", stat.notificationsTrafficPct], ["Others", stat.othersTrafficPct]]
      .filter((x): x is [string, number] => x[1] != null)
      .sort((a, b) => b[1] - a[1])
  ) : [];
  const maxAgeVal = ageBuckets.length ? Math.max(...ageBuckets.map(([, v]) => v)) : 1;
  const maxTrafficVal = trafficSources.length ? Math.max(...trafficSources.map(([, v]) => v)) : 1;
  const chartColors = branding.colors.chart;

  const ytStat = (label: string, value: string | number | undefined, sub?: string) => (
    <div className="rounded-xl border border-border/40 bg-muted/10 p-3 space-y-0.5">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-xl font-bold text-foreground">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            {showZoom ? (
              <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v5.5l4-2.8v6.6l-4-2.8V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6z"/></svg>
            ) : (
              <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z"/></svg>
            )}
            YouTube / Zoom Analytics
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {showZoom ? "Zoom webinar data from attendee report." : "Manually entered from YouTube Studio per day."}
          </p>
        </div>
        {!showZoom && (
          <button onClick={() => onEdit(viewDay)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[var(--brand-primary)]/40 text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
            {stat ? "Edit" : "Add Data"} — {dayLabel}
          </button>
        )}
      </div>

      {/* Day tabs */}
      {days.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {days.map(d => {
            const hasStat = youtubeStats.some(s => s.day === d.day);
            const hasZoom = !hasStat && !!zoomSession;
            return (
              <button key={d.day} onClick={() => setYtViewDay(d.day)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${viewDay === d.day ? "bg-[var(--brand-primary)] text-white border-transparent" : "border-border text-muted-foreground hover:border-[var(--brand-primary)]/50"}`}
              >
                {d.label}
                {hasStat && <span className={`w-1.5 h-1.5 rounded-full ${viewDay === d.day ? "bg-white/60" : "bg-emerald-400"}`} />}
                {hasZoom && <span className={`w-1.5 h-1.5 rounded-full ${viewDay === d.day ? "bg-white/60" : "bg-blue-400"}`} />}
              </button>
            );
          })}
        </div>
      )}

      {showZoom && zoomSession ? (
        <>
          {/* Zoom inline view — shown when the selected day has no YouTube stat but has Zoom data */}
          <div className="rounded-xl bg-muted/20 border border-border/40 px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{zoomSession.topic}</span>
            <span>{new Date(zoomSession.startTime).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
            <span>{zoomSession.durationMinutes}m scheduled</span>
            <span className="text-muted-foreground/50">ID: {zoomSession.webinarId}</span>
          </div>
          {/* Funnel bridge */}
          {optins > 0 && (
            <div className="rounded-xl bg-muted/20 border border-border/40 p-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-blue-400 font-semibold">{zoomSession.uniqueViewers.toLocaleString()} viewers</span>
              <span className="text-muted-foreground">→</span>
              <span className="text-[var(--brand-primary)] font-semibold">{optins.toLocaleString()} opt-ins</span>
              <span className="text-muted-foreground text-xs">({((optins / zoomSession.uniqueViewers) * 100).toFixed(1)}% viewer→optin)</span>
            </div>
          )}
          {/* KPIs */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {ytStat("Registrants", zoomSession.registrants.toLocaleString())}
            {ytStat("Attended", zoomSession.uniqueViewers.toLocaleString(), `${zoomSession.totalSessions} joins`)}
            {ytStat("Show Rate", `${zoomShowRate}%`, "of registrants")}
            {ytStat("Max Concurrent", zoomSession.maxConcurrent.toLocaleString(), "peak")}
            {ytStat("Avg Time", `${zoomAvgMinutes}m`, "per attendee")}
          </div>
          {/* Retention Curve */}
          {zoomSession.retentionCurve.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Retention Curve</p>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={zoomSession.retentionCurve} margin={{ top: 4, right: 4, bottom: 16, left: 0 }}>
                    <defs>
                      <linearGradient id="zoomRetGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={chartColors[0]} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={chartColors[0]} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="minute" tick={{ fontSize: 10 }} tickLine={false} axisLine={false}
                      label={{ value: "Minutes into session", position: "insideBottom", offset: -8, fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={32} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      formatter={(v: number) => [v, "Viewers"]} labelFormatter={(l: number) => `Minute ${l}`} />
                    <Area type="monotone" dataKey="count" stroke={chartColors[0]} strokeWidth={2} fill="url(#zoomRetGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {/* Duration + Country */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {zoomSession.durationBuckets.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Time in Session</p>
                <div className="space-y-1.5">
                  {zoomSession.durationBuckets.map((b, i) => (
                    <div key={b.label} className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-16 shrink-0">{b.label}</span>
                      <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                        <div className="h-full rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${zoomMaxBucket > 0 ? (b.count / zoomMaxBucket) * 100 : 0}%`, background: chartColors[i % chartColors.length] }}>
                          {b.count > 0 && <span className="text-[10px] font-bold text-white">{b.count}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {zoomSession.countryBreakdown.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Top Countries</p>
                <div className="space-y-1.5">
                  {zoomSession.countryBreakdown.slice(0, 8).map((c, i) => (
                    <div key={c.country} className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-24 shrink-0 truncate">{c.country || "Unknown"}</span>
                      <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                        <div className="h-full rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${zoomMaxCountry > 0 ? (c.count / zoomMaxCountry) * 100 : 0}%`, background: chartColors[i % chartColors.length] }}>
                          <span className="text-[10px] font-bold text-white">{c.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Top Attendees */}
          {zoomTopAttendees.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Top Attendees by Time</p>
              <div className="rounded-xl border border-border/40 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/20 border-b border-border/40">
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">#</th>
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">Name</th>
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium hidden sm:table-cell">Email</th>
                      <th className="text-right px-3 py-2 text-muted-foreground font-medium">Time</th>
                      <th className="text-right px-3 py-2 text-muted-foreground font-medium">Joins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zoomTopAttendees.map((a, i) => (
                      <tr key={`${a.email}-${i}`} className="border-b border-border/20 last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="px-3 py-2 text-muted-foreground/50">{i + 1}</td>
                        <td className="px-3 py-2 font-medium truncate max-w-[140px]">{a.name || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px] hidden sm:table-cell">{a.email}</td>
                        <td className="px-3 py-2 text-right font-semibold">{a.totalMinutes}m</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{a.sessions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : !stat ? (
        <div className="text-center py-10 text-muted-foreground text-sm">
          <p>No data for {dayLabel} yet.</p>
        </div>
      ) : (
        <>
          {/* Funnel bridge */}
          {(stat.peakViewers || stat.uniqueViewers) && optins > 0 && (
            <div className="rounded-xl bg-muted/20 border border-border/40 p-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-red-400 font-semibold">{(stat.peakViewers ?? stat.uniqueViewers ?? 0).toLocaleString()} viewers</span>
              <span className="text-muted-foreground">→</span>
              <span className="text-[var(--brand-primary)] font-semibold">{optins.toLocaleString()} opt-ins</span>
              {optins > 0 && stat.peakViewers && <span className="text-muted-foreground text-xs">({((optins / stat.peakViewers) * 100).toFixed(1)}% viewer→optin)</span>}
              {apps > 0 && <>
                <span className="text-muted-foreground">→</span>
                <span className="text-emerald-400 font-semibold">{apps.toLocaleString()} apps</span>
              </>}
            </div>
          )}

          {/* Live */}
          {(stat.peakViewers || stat.avgViewers || stat.streamDurationSecs) && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Live Stream</p>
              <div className="grid grid-cols-3 gap-3">
                {ytStat("Peak Viewers", stat.peakViewers?.toLocaleString())}
                {ytStat("Avg Viewers", stat.avgViewers?.toLocaleString())}
                {ytStat("Duration", fmtDuration(stat.streamDurationSecs))}
              </div>
            </div>
          )}

          {/* Reach */}
          {(stat.impressions || stat.views) && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Reach</p>
              <div className="grid grid-cols-2 gap-3">
                {ytStat("Impressions", stat.impressions?.toLocaleString())}
                {ytStat("Views", stat.views?.toLocaleString())}
              </div>
            </div>
          )}

          {/* Engagement */}
          {(stat.impressionsCtr != null || stat.avgViewDurationSecs || stat.avgPctViewed) && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Engagement</p>
              <div className="grid grid-cols-3 gap-3">
                {ytStat("Impressions CTR", stat.impressionsCtr != null ? `${stat.impressionsCtr}%` : undefined)}
                {ytStat("Avg View Duration", fmtDuration(stat.avgViewDurationSecs))}
                {ytStat("Avg % Viewed", stat.avgPctViewed != null ? `${stat.avgPctViewed}%` : undefined)}
              </div>
            </div>
          )}

          {/* Audience */}
          {(stat.newViewersPct != null || stat.casualViewersPct != null || stat.regularViewersPct != null) && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Audience</p>
              <div className="flex rounded-lg overflow-hidden h-8 gap-px">
                {stat.newViewersPct != null && (
                  <div className="flex items-center justify-center bg-violet-400/60" style={{ width: `${stat.newViewersPct}%` }}>
                    {stat.newViewersPct > 10 && <span className="text-[10px] font-semibold text-white drop-shadow-sm">New</span>}
                  </div>
                )}
                {stat.casualViewersPct != null && (
                  <div className="flex items-center justify-center bg-violet-500" style={{ width: `${stat.casualViewersPct}%` }}>
                    {stat.casualViewersPct > 10 && <span className="text-[10px] font-semibold text-white drop-shadow-sm">Casual</span>}
                  </div>
                )}
                {stat.regularViewersPct != null && (
                  <div className="flex items-center justify-center bg-violet-800" style={{ width: `${stat.regularViewersPct}%` }}>
                    {stat.regularViewersPct > 10 && <span className="text-[10px] font-semibold text-white drop-shadow-sm">Regular</span>}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-[10px] text-muted-foreground">
                {stat.newViewersPct != null && <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-violet-400/60 shrink-0" />New — {stat.newViewersPct}%</span>}
                {stat.casualViewersPct != null && <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-violet-500 shrink-0" />Casual — {stat.casualViewersPct}%</span>}
                {stat.regularViewersPct != null && <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-violet-800 shrink-0" />Regular — {stat.regularViewersPct}%</span>}
              </div>
            </div>
          )}

          {/* Demographics */}
          {(stat.malePct != null || stat.femalePct != null || stat.subscribedPct != null || ageBuckets.length > 0) && (
            <div className="space-y-4">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Demographics</p>

              {/* Gender + Subscribers */}
              {(stat.malePct != null || stat.femalePct != null || stat.subscribedPct != null || stat.notSubscribedPct != null) && (
                <div className="grid grid-cols-2 gap-5">
                  {(stat.malePct != null || stat.femalePct != null) && (
                    <div className="space-y-2">
                      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Gender</p>
                      <div className="space-y-1.5">
                        {stat.malePct != null && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-10 shrink-0">Male</span>
                            <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full flex items-center justify-end pr-2" style={{ width: `${stat.malePct}%` }}>
                                <span className="text-[10px] font-bold text-white">{stat.malePct}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {stat.femalePct != null && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-10 shrink-0">Female</span>
                            <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                              <div className="h-full bg-pink-400 rounded-full flex items-center justify-end pr-2" style={{ width: `${stat.femalePct}%` }}>
                                <span className="text-[10px] font-bold text-white">{stat.femalePct}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {(stat.subscribedPct != null || stat.notSubscribedPct != null) && (
                    <div className="space-y-2">
                      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Subscribers</p>
                      <div className="space-y-1.5">
                        {stat.subscribedPct != null && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-14 shrink-0">Subbed</span>
                            <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                              <div className="h-full bg-red-500 rounded-full flex items-center justify-end pr-2" style={{ width: `${stat.subscribedPct}%` }}>
                                <span className="text-[10px] font-bold text-white">{stat.subscribedPct}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {stat.notSubscribedPct != null && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-14 shrink-0">Not Sub.</span>
                            <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                              <div className="h-full bg-slate-500 rounded-full flex items-center justify-end pr-2" style={{ width: `${stat.notSubscribedPct}%` }}>
                                <span className="text-[10px] font-bold text-white">{stat.notSubscribedPct}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Age Breakdown */}
              {ageBuckets.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Age Breakdown</p>
                  <div className="space-y-1.5">
                    {ageBuckets.map(([label, val]) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-10 shrink-0">{label}</span>
                        <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                          <div
                            className="h-full rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${(val / maxAgeVal) * 100}%`, background: "var(--brand-primary)" }}
                          >
                            <span className="text-[10px] font-bold text-white">{val}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Traffic Sources */}
          {trafficSources.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Traffic Sources</p>
              <div className="space-y-1.5">
                {trafficSources.map(([label, val], i) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-24 shrink-0">{label}</span>
                    <div className="flex-1 bg-muted/30 rounded-full h-5 overflow-hidden">
                      <div
                        className="h-full rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(val / maxTrafficVal) * 100}%`, background: chartColors[i % chartColors.length] }}
                      >
                        <span className="text-[10px] font-bold text-white">{val}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function EventListView({ events, onSelect }: { events: EventDef[]; onSelect: (id: string) => void; }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Events</h1>
        <p className="text-sm text-muted-foreground">Select an event to view detailed analytics</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map(event => {
          const logo = EVENT_LOGOS[event.id];
          const startDate = event.days[0]
            ? new Date(event.days[0].date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
          const endDate = event.days[event.days.length - 1]
            ? new Date(event.days[event.days.length - 1].date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

          return (
            <motion.button key={event.id} onClick={() => onSelect(event.id)}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className="text-left rounded-2xl border border-border/60 bg-card p-6 hover:border-[var(--brand-primary)]/50 hover:shadow-lg transition-all group cursor-pointer"
            >
              <div className="h-36 rounded-xl mb-4 overflow-hidden flex items-center justify-center bg-[#0a0a0a] border border-border/30">
                {logo ? (
                  <img src={logo} alt={event.name} className="w-full h-full object-contain p-3" />
                ) : (
                  <div className="bg-gradient-to-br from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] w-full h-full flex items-center justify-center">
                    <div className="text-white text-center">
                      <div className="text-2xl font-bold">{event.name}</div>
                      <div className="text-xs opacity-70 mt-1 uppercase tracking-wider">{event.platform}</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{event.name}</span>
                  <span className="text-xs text-muted-foreground bg-muted/40 px-2 py-0.5 rounded-full">{event.days.length} days</span>
                </div>
                {startDate && endDate && (
                  <p className="text-xs text-muted-foreground">{startDate === endDate ? startDate : `${startDate} – ${endDate}`}</p>
                )}
                <div className="flex items-center gap-1 text-xs text-[var(--brand-primary)] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>View Analytics</span><span>→</span>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Custom bar label for cash chart ─────────────────────────────────────────

function CashBarLabel(props: { x?: number; y?: number; width?: number; height?: number; value?: number }) {
  const { x = 0, y = 0, width = 0, height = 0, value = 0 } = props;
  if (width < 40) return null;
  return (
    <text
      x={x + width / 2} y={y + height / 2 + 4}
      textAnchor="middle" fill="white"
      fontSize={11} fontWeight="bold"
      style={{ pointerEvents: "none" }}
    >
      {fmtCash(value)}
    </text>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Events() {
  const [events, setEvents] = useState<EventDef[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [funnel, setFunnel] = useState<EventFunnel | null>(null);
  const [cashBySource, setCashBySource] = useState<CashBySourceItem[]>([]);
const [investmentStats, setInvestmentStats] = useState<InvestmentStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number>(0);
  const [adSpendInput, setAdSpendInput] = useState("");
  const [savingAdSpend, setSavingAdSpend] = useState(false);
  const [paidCashInput, setPaidCashInput] = useState("");
  const [savingPaidCash, setSavingPaidCash] = useState(false);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());
  const [stageModal, setStageModal] = useState<{ stage: string; label: string } | null>(null);
const [configuringDays, setConfiguringDays] = useState(false);
  const [youtubeStats, setYoutubeStats] = useState<YouTubeDayStats[]>([]);
  const [editingYoutubeDay, setEditingYoutubeDay] = useState<number | null>(null);
  const [optinsByDay, setOptinsByDay] = useState<Record<number, number>>({});
  const [appsByDay, setAppsByDay] = useState<Record<number, number>>({});
  const [zoomSession, setZoomSession] = useState<ZoomSessionData | null>(null);

  const toggleMetric = (label: string) => {
    setHiddenMetrics(prev => { const n = new Set(prev); n.has(label) ? n.delete(label) : n.add(label); return n; });
  };

  function reloadEvents() {
    fetch("/api/events/list").then(r => r.json())
      .then((d: { success: boolean; events: EventDef[] }) => { if (d.success) setEvents(d.events); })
      .catch(console.error);
  }

  useEffect(() => {
    fetch("/api/events/list").then(r => r.json())
      .then((d: { success: boolean; events: EventDef[] }) => { if (d.success) setEvents(d.events); })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    fetch(`/api/events/investment-stats/${selectedId}`)
      .then(r => r.json())
      .then((d: { success: boolean; stats: InvestmentStats }) => { if (d.success) setInvestmentStats(d.stats); })
      .catch(console.error);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    fetch(`/api/events/youtube/${selectedId}`)
      .then(r => r.json())
      .then((d: { success: boolean; stats: YouTubeDayStats[] }) => { if (d.success) setYoutubeStats(d.stats); })
      .catch(console.error);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    fetch(`/api/events/optin-counts/${selectedId}`)
      .then(r => r.json())
      .then((d: { success: boolean; counts: Record<number, number> }) => { if (d.success) setOptinsByDay(d.counts); })
      .catch(console.error);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    fetch(`/api/events/app-counts/${selectedId}`)
      .then(r => r.json())
      .then((d: { success: boolean; counts: Record<number, number> }) => { if (d.success) setAppsByDay(d.counts); })
      .catch(console.error);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    fetch(`/api/events/zoom/${selectedId}`)
      .then(r => r.json())
      .then((d: { success: boolean; session: ZoomSessionData | null }) => { if (d.success) setZoomSession(d.session); })
      .catch(console.error);
  }, [selectedId]);

  const loadEvent = useCallback(async (id: string, day = 0) => {
    setLoading(true); setFunnel(null); setCashBySource([]);
    const dayParam = day > 0 ? `?day=${day}` : "";
    try {
      const [fRes, cbsRes] = await Promise.all([
        fetch(`/api/events/funnel/${id}${dayParam}`).then(r => r.json()),
        fetch(`/api/events/cash-by-source/${id}${dayParam}`).then(r => r.json()),
      ]);
      if (fRes.success) {
        setFunnel(fRes.funnel);
        if (fRes.funnel.adSpend > 0) setAdSpendInput(String(fRes.funnel.adSpend));
        // Load manual paid cash override separately
        fetch(`/api/events/paid-cash/${id}`).then(r => r.json()).then((pc: { success: boolean; paidCash: number | null }) => {
          if (pc.success && pc.paidCash != null) setPaidCashInput(String(pc.paidCash));
          else setPaidCashInput("");
        }).catch(() => {});
      }
      if (cbsRes.success) setCashBySource(cbsRes.items);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { if (selectedId) loadEvent(selectedId, selectedDay); }, [selectedId, selectedDay, loadEvent]);

  async function saveAdSpend() {
    if (!selectedId) return;
    const amount = parseFloat(adSpendInput);
    if (isNaN(amount) || amount < 0) return;
    setSavingAdSpend(true);
    await fetch(`/api/events/spend/${selectedId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adSpend: amount }),
    });
    setSavingAdSpend(false);
    loadEvent(selectedId, selectedDay);
  }

  async function savePaidCash() {
    if (!selectedId) return;
    const amount = parseFloat(paidCashInput);
    if (isNaN(amount) || amount < 0) return;
    setSavingPaidCash(true);
    await fetch(`/api/events/paid-cash/${selectedId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paidCash: amount }),
    });
    setSavingPaidCash(false);
    loadEvent(selectedId, selectedDay);
  }

  function handleSelectEvent(id: string) {
    setSelectedId(id); setSelectedDay(0);
    setFunnel(null); setCashBySource([]);
    setInvestmentStats(null); setHiddenMetrics(new Set()); setConfiguringDays(false);
    setYoutubeStats([]); setOptinsByDay({}); setAppsByDay({}); setZoomSession(null);
  }

  const currentEvent = events.find(e => e.id === selectedId);
  // ── Metric cards ─────────────────────────────────────────────────────────────
  type CardDef =
    | { type: "standard"; label: string; value: string; sub?: string; highlight?: boolean; infoKey: string; clickStage?: "optins" | "apps" | "purchases" }
    | { type: "apps"; label: string; value: string; sub?: string; infoKey: string; fullApps: number; partialApps: number; clickStage: "apps" }
    | { type: "split"; label: string; infoKey: string; tabA: string; valA: string; tabB: string; valB: string; subA?: string; subB?: string }
    | { type: "calls" }
    | { type: "calendar" };

  const metricCards: CardDef[] = funnel
    ? [
        // ── Core counts
        { type: "standard", label: "Total Opt-ins", value: fmt(funnel.totalOptins), sub: `${fmt(funnel.uniqueOptins)} unique`, infoKey: "Total Opt-ins" },
        { type: "standard", label: "Unique Opt-ins", value: fmt(funnel.uniqueOptins), infoKey: "Unique Opt-ins", clickStage: "optins" },
        { type: "apps", label: "Applications", value: fmt(funnel.totalApps), sub: fmtPct(funnel.optinToAppRate) + " of opt-ins", infoKey: "Applications", clickStage: "apps", fullApps: funnel.fullApps, partialApps: funnel.partialApps },
        { type: "standard", label: "Closes", value: fmt(funnel.totalPurchases + funnel.totalCloses), sub: fmtPct(funnel.optinToPurchaseRate + funnel.optinToCloseRate) + " of opt-ins", infoKey: "Closes", clickStage: "purchases" },
        // ── Calls card: Taken | Closed | Do
        { type: "calls" },
        // ── Cash
        { type: "standard", label: "Cash Collected", value: fmtCashFull(funnel.totalCash), highlight: true, infoKey: "Cash Collected", clickStage: "purchases" },
        { type: "standard", label: "Cash / Opt-in", value: fmtCash(funnel.cashPerOptin), infoKey: "Cash / Opt-in" },
        { type: "standard", label: "Cash / Application", value: funnel.totalApps > 0 ? fmtCash(funnel.cashPerApp) : "—", infoKey: "Cash / Application" },
        // ── Ad spend / ROAS
        { type: "standard", label: "Ad Spend", value: funnel.adSpend > 0 ? fmtCashFull(funnel.adSpend) : "—", infoKey: "Ad Spend" },
        ...(funnel.adSpend > 0 ? [
          { type: "standard" as const, label: "ROAS", value: fmtX(funnel.roas), highlight: funnel.roas >= 1, sub: `${fmtCash(funnel.paidCash)} paid cash`, infoKey: "ROAS" },
          { type: "standard" as const, label: "Cost / Opt-in", value: fmtCash(funnel.costPerOptin), infoKey: "Cost / Opt-in" },
        ] : []),
        // ── Bookings
        { type: "standard", label: "Total Bookings", value: fmt(funnel.totalCalls), sub: fmtPct(funnel.optinToCallRate) + " of opt-ins", infoKey: "Total Bookings" },
        { type: "split", label: "Booking Rate", infoKey: "Booking Rate", tabA: "Opt-in → Book", valA: fmtPct(funnel.optinToCallRate), tabB: "App → Book", valB: fmtPct(funnel.appToCallRate) },
        // ── Call rates (grouped)
        { type: "standard", label: "Show Rate", value: funnel.totalCalls > 0 ? fmtPct(funnel.showRate) : "—", sub: funnel.totalCalls > 0 ? `${fmt(funnel.totalShowed)}/${fmt(funnel.totalCalls)} calls` : undefined, infoKey: "Show Rate" },
        { type: "standard", label: "Close Rate", value: funnel.totalShowed > 0 ? fmtPct(funnel.closeRate) : "—", sub: funnel.totalCloses > 0 ? `${fmt(funnel.totalCloses)} closes` : undefined, infoKey: "Close Rate" },
        { type: "standard", label: "AOV", value: fmtCash(funnel.aov), sub: `avg per close`, infoKey: "AOV" },
        { type: "standard", label: "Avg Cash / Booking", value: funnel.totalCalls > 0 ? fmtCash(funnel.avgCashPerBooking) : "—", infoKey: "Avg Cash / Booking" },
        // ── Close rates
        { type: "standard", label: "Opt-in → Close %", value: fmtPct(funnel.optinToCloseRate), infoKey: "Opt-in → Close %" },
        { type: "standard", label: "App → Close %", value: funnel.totalApps > 0 ? fmtPct(funnel.appToCloseRate) : "—", infoKey: "App → Close %" },
        { type: "standard", label: "Opt-in → App %", value: fmtPct(funnel.optinToAppRate), infoKey: "Opt-in → App %" },
        // ── Calendar adds (special card)
        { type: "calendar" },
      ]
    : [];

  const visibleCards = metricCards.filter(c => c.type === "calendar" || c.type === "calls" || !hiddenMetrics.has((c as { label?: string }).label ?? ""));

  // ── Funnel stages ─────────────────────────────────────────────────────────

  const funnelStages: FunnelStageData[] = funnel
    ? [
        { label: "Unique Opt-ins", value: funnel.uniqueOptins, onClick: () => setStageModal({ stage: "optins", label: "Opt-ins" }) },
        { label: "Applications", value: funnel.totalApps, subLabel: `${fmtPct(funnel.optinToAppRate)} of opt-ins`, onClick: () => setStageModal({ stage: "apps", label: "Applications" }) },
        ...(funnel.totalCalls > 0 ? [{ label: "Calls Booked", value: funnel.totalCalls, subLabel: `${fmtPct(funnel.optinToCallRate)} booking rate` }] : []),
        ...(funnel.totalShowed > 0 ? [{ label: "Calls Taken", value: funnel.totalShowed, subLabel: `${fmtPct(funnel.showRate)} show rate` }] : []),
        ...(funnel.totalCloses > 0 ? [{ label: "Closed", value: funnel.totalCloses, subLabel: `${fmtPct(funnel.closeRate)} close rate` }] : []),
        { label: "Cash Collected", value: funnel.totalCash, isCash: true, onClick: () => setStageModal({ stage: "purchases", label: "Cash — Purchases" }) },
      ]
    : [];

  // ── List view ────────────────────────────────────────────────────────────────
  if (!selectedId) {
    return (
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <EventListView events={events} onSelect={handleSelectEvent} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedId(null)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
            ← Events
          </button>
          <span className="text-muted-foreground/40">/</span>
          <h1 className="text-lg font-bold">{currentEvent?.name ?? selectedId}</h1>
        </div>
        <div className="flex items-center gap-2">
          {events.length > 1 && (
            <select value={selectedId ?? ""} onChange={e => handleSelectEvent(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-[var(--brand-primary)]/50"
            >
              {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          )}
          <button onClick={() => setConfiguringDays(v => !v)} title="Configure event days"
            className={`p-2 rounded-lg border transition-all ${configuringDays ? "border-[var(--brand-primary)]/50 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]" : "border-border text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/30"}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {configuringDays && currentEvent && (
          <ConfigureDaysPanel
            eventId={currentEvent.id} currentDays={currentEvent.days}
            onSaved={days => { setEvents(prev => prev.map(e => e.id === currentEvent.id ? { ...e, days } : e)); reloadEvents(); }}
            onClose={() => setConfiguringDays(false)}
          />
        )}
      </AnimatePresence>

      {/* Day tabs */}
      {currentEvent && currentEvent.days.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={() => setSelectedDay(0)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${selectedDay === 0 ? "bg-[var(--brand-primary)] text-white border-transparent" : "border-border text-muted-foreground hover:border-[var(--brand-primary)]/50 hover:text-foreground"}`}
          >All Days</button>
          {currentEvent.days.map(d => (
            <button key={d.day} onClick={() => setSelectedDay(d.day)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${selectedDay === d.day ? "bg-[var(--brand-primary)] text-white border-transparent" : "border-border text-muted-foreground hover:border-[var(--brand-primary)]/50 hover:text-foreground"}`}
            >
              {d.label}
              <span className="ml-1 text-[10px] opacity-60">{new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            </button>
          ))}
          {selectedDay > 0 && <span className="text-xs text-muted-foreground ml-1">— apps, calls & closes on this day</span>}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
        </div>
      )}

      {!loading && funnel && (
        <>
          {/* ── Sales Metrics ── */}
          <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold">Sales Metrics</h2>
                {hiddenMetrics.size > 0 && (
                  <button onClick={() => setHiddenMetrics(new Set())}
                    className="text-xs px-2 py-0.5 rounded-full border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 transition-colors"
                  >↺ Reset ({hiddenMetrics.size})</button>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Ad Spend:</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">$</span>
                    <input type="number" min="0" value={adSpendInput} onChange={e => setAdSpendInput(e.target.value)}
                      onBlur={saveAdSpend} onKeyDown={e => e.key === "Enter" && saveAdSpend()} placeholder="0"
                      className="w-24 px-2 py-1 text-xs rounded-lg border border-border bg-muted/20 focus:outline-none focus:border-[var(--brand-primary)]/50"
                    />
                    <button onClick={saveAdSpend} disabled={savingAdSpend}
                      className="text-xs px-2 py-1 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/20 disabled:opacity-50"
                    >{savingAdSpend ? "…" : "Save"}</button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Paid Cash (Meta):</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">$</span>
                    <input type="number" min="0" value={paidCashInput} onChange={e => setPaidCashInput(e.target.value)}
                      onBlur={savePaidCash} onKeyDown={e => e.key === "Enter" && savePaidCash()} placeholder="from ads"
                      className="w-28 px-2 py-1 text-xs rounded-lg border border-border bg-muted/20 focus:outline-none focus:border-[var(--brand-primary)]/50"
                    />
                    <button onClick={savePaidCash} disabled={savingPaidCash}
                      className="text-xs px-2 py-1 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/20 disabled:opacity-50"
                    >{savingPaidCash ? "…" : "Save"}</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {visibleCards.map((card, idx) => {
                if (card.type === "calendar") {
                  return (
                    <CalendarAddsCard
                      key="calendar"
                      eventId={selectedId!}
                      uniqueOptins={funnel.uniqueOptins}
                      onHide={() => toggleMetric("Add to Calendar")}
                      infoText={INFO_TEXT["Add to Calendar"]}
                    />
                  );
                }
                if (card.type === "calls") {
                  return (
                    <CallsCard
                      key="calls"
                      scheduled={funnel.totalCalls}
                      taken={funnel.totalShowed}
                      closed={funnel.totalCloses}
                      closeRate={funnel.closeRate}
                      showRate={funnel.showRate}
                      onHide={() => toggleMetric("Calls")}
                      infoText={INFO_TEXT["Calls"]}
                    />
                  );
                }
                if (card.type === "apps") {
                  return (
                    <AppsCard
                      key={card.label}
                      label={card.label} value={card.value} sub={card.sub}
                      fullApps={card.fullApps} partialApps={card.partialApps}
                      infoText={INFO_TEXT[card.infoKey]}
                      onHide={() => toggleMetric(card.label)}
                      onClick={() => setStageModal({ stage: card.clickStage, label: card.label })}
                    />
                  );
                }
                if (card.type === "split") {
                  return (
                    <SplitCard
                      key={card.label}
                      label={card.label} tabA={card.tabA} valA={card.valA} tabB={card.tabB} valB={card.valB}
                      subA={card.subA} subB={card.subB}
                      infoText={INFO_TEXT[card.infoKey]}
                      onHide={() => toggleMetric(card.label)}
                    />
                  );
                }
                return (
                  <MetricCard
                    key={card.label + idx}
                    label={card.label} value={card.value} sub={card.sub} highlight={card.highlight}
                    infoText={INFO_TEXT[card.infoKey]}
                    onHide={() => toggleMetric(card.label)}
                    onClick={card.clickStage ? () => setStageModal({ stage: card.clickStage!, label: card.label }) : undefined}
                  />
                );
              })}
            </div>
          </div>

          {/* ── Funnel ── */}
          <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
            <h2 className="text-base font-semibold">Funnel</h2>
            <VisualFunnel stages={funnelStages} />
            {funnel.adSpend > 0 && (
              <div className="pt-2 border-t border-border/40 text-xs text-muted-foreground flex gap-4">
                <span>ROAS <span className="text-foreground font-semibold">{fmtX(funnel.roas)}</span></span>
                <span>Ad Spend <span className="text-foreground font-semibold">{fmtCashFull(funnel.adSpend)}</span></span>
                <span>Cost/Optin <span className="text-foreground font-semibold">{fmtCash(funnel.costPerOptin)}</span></span>
              </div>
            )}
          </div>

          {/* ── Cash by Traffic Source ── */}
          {cashBySource.length > 0 && (
            <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
              <h2 className="text-base font-semibold">Cash by Traffic Source</h2>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cashBySource} margin={{ left: 10, right: 10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis
                      dataKey="source"
                      tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }}
                      angle={-30} textAnchor="end" interval={0}
                    />
                    <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v: number) => fmtCash(v)} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "rgba(255,255,255,0.9)" }}
                      labelStyle={{ color: "rgba(255,255,255,0.9)" }}
                      itemStyle={{ color: "rgba(255,255,255,0.8)" }}
                      formatter={(v: number, name: string) => [name === "cash" ? fmtCashFull(v) : fmt(v), name === "cash" ? "Cash" : "Closes"]}
                    />
                    <Bar dataKey="cash" radius={[4, 4, 0, 0]}>
                      {cashBySource.map((_, i) => (
                        <Cell key={i} fill={branding.colors.chart[i % branding.colors.chart.length]} />
                      ))}
                      <LabelList content={<CashBarLabel />} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5">
                {cashBySource.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm px-3 py-1.5 rounded-lg bg-muted/20">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: branding.colors.chart[i % branding.colors.chart.length] }} />
                      <span className="text-muted-foreground">{item.source}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-muted-foreground">{item.purchases} closes</span>
                      <span className="font-semibold text-white">{fmtCashFull(item.cash)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Opt-ins by Source ── */}
          {funnel.sourceBreakdown.length > 0 && (
            <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
              <h2 className="text-base font-semibold">Opt-ins by Traffic Source</h2>
              <div className="space-y-1.5">
                {funnel.sourceBreakdown.slice(0, 10).map((item, i) => {
                  const pct = funnel.uniqueOptins > 0 ? (item.optins / funnel.uniqueOptins) * 100 : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-24 text-right text-xs text-muted-foreground shrink-0">{item.source}</div>
                      <div className="flex-1 h-5 rounded bg-muted/30 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4, ease: "easeOut" }}
                          className="h-full rounded" style={{ background: branding.colors.chart[i % branding.colors.chart.length] }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground w-20 shrink-0">
                        {fmt(item.optins)} <span className="opacity-60">({fmtPct(pct)})</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Applications Over Time ── */}
          {investmentStats && (
            <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold">TOTAL APPLICATIONS OVER TIME</h2>
                <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted">Daily</span>
              </div>
              <div className="h-[280px]">
                {investmentStats.dailyAppTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={investmentStats.dailyAppTrend} margin={{ top: 20, right: 20, bottom: 20, left: 10 }}>
                      <defs>
                        <linearGradient id="appsGradEvent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={branding.colors.gradient.start} stopOpacity={0.4} />
                          <stop offset="50%" stopColor={branding.colors.gradient.start} stopOpacity={0.15} />
                          <stop offset="100%" stopColor={branding.colors.gradient.start} stopOpacity={0} />
                        </linearGradient>
                        <filter id="appsGlow">
                          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
                        </filter>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" strokeOpacity={0.1} />
                      <XAxis
                        dataKey="date"
                        axisLine={false} tickLine={false}
                        tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
                        dy={10}
                        interval={Math.max(0, Math.floor(investmentStats.dailyAppTrend.length / 8) - 1)}
                      />
                      <YAxis
                        axisLine={false} tickLine={false}
                        tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
                        tickFormatter={(v: number) => v.toLocaleString()}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "rgba(255,255,255,0.9)" }}
                        formatter={(v: number) => [v.toLocaleString(), "Applications"]}
                        cursor={{ stroke: branding.colors.gradient.start, strokeWidth: 1, strokeDasharray: "4 4", strokeOpacity: 0.5 }}
                      />
                      <Area type="monotone" dataKey="total" fill="url(#appsGradEvent)" stroke="none" animationDuration={1200} />
                      <Line
                        type="monotone" dataKey="total"
                        stroke={branding.colors.gradient.start}
                        strokeWidth={3} dot={false}
                        filter="url(#appsGlow)"
                        activeDot={{ fill: branding.colors.gradient.start, strokeWidth: 3, stroke: "#ffffff", r: 6, filter: "url(#appsGlow)" }}
                        animationDuration={1200}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">No application data yet</div>
                )}
              </div>
            </div>
          )}

          {/* ── Investment Bucket Charts ── */}
          {investmentStats && (
            <>
              {/* Applicant Investment Ability bar chart */}
              <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
                <h2 className="text-base font-semibold">APPLICANT INVESTMENT ABILITY</h2>
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={investmentStats.applicantIncomeBreakdown}
                      margin={{ top: 32, right: 24, bottom: 16, left: 8 }}
                      barCategoryGap="28%"
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" strokeOpacity={0.08} />
                      <XAxis
                        dataKey="name"
                        axisLine={false} tickLine={false}
                        tick={{ fontSize: 12, fill: "currentColor", opacity: 0.65, fontWeight: 500 }}
                        tickMargin={10}
                        tickFormatter={(v: string) => v.replace(" (We cannot help you)", "")}
                      />
                      <YAxis
                        allowDecimals={false}
                        axisLine={false} tickLine={false}
                        tick={{ fontSize: 12, fill: "currentColor", opacity: 0.55 }}
                        width={36}
                      />
                      <Tooltip
                        cursor={{ fill: "currentColor", fillOpacity: 0.06, radius: 8 }}
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12, color: "rgba(255,255,255,0.9)" }}
                        formatter={(v: number) => [`${v.toLocaleString()} applicants`, ""]}
                      />
                      <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                        {investmentStats.applicantIncomeBreakdown.map((_, index) => (
                          <Cell key={`ev-income-${index}`} fill={branding.colors.chart[index % branding.colors.chart.length]} />
                        ))}
                        <LabelList dataKey="value" position="top" style={{ fontSize: 13, fontWeight: 700, fill: "currentColor", opacity: 0.8 }} formatter={(v: number) => v > 0 ? v : ""} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Investment close rate gauges */}
              {investmentStats.investmentCloseRates.some(r => r.total > 0) && (
                <div className="rounded-2xl border border-border/60 bg-card/50 p-5 space-y-6">
                  {/* Row 1: leads closed */}
                  <div>
                    <div className="mb-3">
                      <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground">LEADS CLOSED BY INVESTMENT BUCKET</h3>
                      <p className="text-xs text-muted-foreground/60 mt-0.5">% of all applications in each bucket that closed (email-matched to EOC)</p>
                    </div>
                    <div className="grid grid-cols-5 gap-4">
                      {investmentStats.investmentCloseRates.map(item => (
                        <InvestmentGauge key={item.bucket} bucket={item.bucket} total={item.total} closed={item.closed} rate={item.rate} sublabel="closed" />
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-border/40" />
                  {/* Row 2: call close rate */}
                  <div>
                    <div className="mb-3">
                      <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground">CALL CLOSE RATE BY INVESTMENT BUCKET</h3>
                      <p className="text-xs text-muted-foreground/60 mt-0.5">% of applicants who booked a call (in EOC) that actually closed</p>
                    </div>
                    <div className="grid grid-cols-5 gap-4">
                      {investmentStats.investmentCloseRates.map(item => (
                        <InvestmentGauge key={item.bucket} bucket={item.bucket} total={item.calledTotal} closed={item.closed} rate={item.callCloseRate} sublabel="call close" totalLabel="calls" />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── YouTube / Zoom Analytics ── */}
      {currentEvent && selectedId && (
        <YouTubeSection
          eventId={selectedId}
          days={currentEvent.days}
          selectedDay={selectedDay}
          youtubeStats={youtubeStats}
          optinsByDay={optinsByDay}
          appsByDay={appsByDay}
          onEdit={day => setEditingYoutubeDay(day)}
          zoomSession={zoomSession}
        />
      )}



      <AnimatePresence>
        {stageModal && selectedId && (
          <StageLeadsModal eventId={selectedId} stage={stageModal.stage} label={stageModal.label} onClose={() => setStageModal(null)} />
        )}
        {editingYoutubeDay !== null && selectedId && currentEvent && (
          <YouTubeEditModal
            eventId={selectedId}
            day={editingYoutubeDay}
            dayLabel={currentEvent.days.find(d => d.day === editingYoutubeDay)?.label ?? `Day ${editingYoutubeDay}`}
            existing={youtubeStats.find(s => s.day === editingYoutubeDay)}
            onClose={() => setEditingYoutubeDay(null)}
            onSaved={stats => {
              setYoutubeStats(prev => [...prev.filter(s => s.day !== stats.day), stats]);
              setEditingYoutubeDay(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
